<script setup>
import { computed, ref } from 'vue';
import Chart from 'primevue/chart';
import MutualFundDialog from '@/components/dashboard/MutualFundDialog.vue';

const props = defineProps({
  answers: { type: Object, required: true },
  result: { type: Object, default: null },
  loading: { type: Boolean, default: false }
});

const emit = defineEmits(['edit']);

const showAnswers = ref(false);

const advice = computed(() => props.result?.mutualFundAdvice || props.result || null);
const anchors = computed(() => advice.value?.anchors || []);
const satellites = computed(() => advice.value?.satellites || []);

const rows = computed(() => {
  const max = Math.max(anchors.value.length, satellites.value.length);
  const out = [];
  for (let i = 0; i < max; i += 1) {
    out.push({
      anchor: anchors.value[i] || null,
      satellite: satellites.value[i] || null
    });
  }
  return out;
});

const pieData = computed(() => {
  const labels = [];
  const data = [];
  const backgroundColor = [];

  anchors.value.forEach(a => {
    labels.push(a.fundName || a.category);
    data.push(a.allocationPercent || 0);
    backgroundColor.push('#3b82f6');
  });
  satellites.value.forEach(s => {
    labels.push(s.fundName || s.category);
    data.push(s.allocationPercent || 0);
    backgroundColor.push('#22c55e');
  });

  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor,
        borderWidth: 0
      }
    ]
  };
});

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '60%',
  plugins: {
    legend: { display: false }
  }
};

const prettyAnswers = computed(() => {
  const a = props.answers || {};
  const goalsArr = Array.isArray(a.goals) ? a.goals : [];

  return [
    { label: 'Goal 1', value: goalsArr[0] ? `${goalsArr[0].name} (${goalsArr[0].horizon})` : 'Not provided' },
    { label: 'Goal 2', value: goalsArr[1] ? `${goalsArr[1].name} (${goalsArr[1].horizon})` : 'Not provided' },
    { label: 'Goal 3', value: goalsArr[2] ? `${goalsArr[2].name} (${goalsArr[2].horizon})` : 'Not provided' },
    { label: 'Risk tolerance', value: a.riskTolerance },
    { label: 'Allocation style', value: a.allocationStyle },
    { label: 'Exclude small-cap', value: a.excludeSmallCap ? 'Yes' : 'No' },
    { label: 'Needs 80C (ELSS)', value: a.taxStatus?.needs80C ? 'Yes' : 'No' },
    { label: 'Income slab', value: a.taxStatus?.incomeSlab },
    { label: 'Income sleeve', value: a.incomeNeed ? 'Yes' : 'No' },
    { label: 'International exposure', value: a.internationalExposure ? 'Yes' : 'No' },
    { label: 'Thematic / focused funds', value: a.thematicInvesting ? 'Yes' : 'No' },
    { label: 'Multi-asset preference', value: a.multiAssetPreference ? 'Yes' : 'No' },
    { label: 'Fixed-income sleeve', value: a.fixedIncomeNeed ? 'Yes' : 'No' },
    { label: 'Arbitrage preference', value: a.arbitragePreference ? 'Yes' : 'No' },
    { label: 'Rate outlook', value: a.rateOutlook },
    { label: 'Market cycle view', value: a.marketCycle },
    { label: 'Diversification preference', value: a.diversificationPreference }
  ];
});

// --- MutualFundDialog logic ---
const isMFDialogOpen = ref(false);
const dialogFund = ref({});
const openMFDialog = (fund) => {
  isMFDialogOpen.value = true;
  dialogFund.value = fund;
};

</script>

<template>
  <MutualFundDialog
    v-if="isMFDialogOpen"
    v-model:isMFDialogOpen="isMFDialogOpen"
    :fund="dialogFund"
  />
  <div class="mf-result-root">
    <div class="mf-result-header">
      <div>
        <h2 class="mf-title">Your recommended portfolio</h2>
        <p class="mf-subtitle">
          Anchors provide the core stability; satellites add targeted tilts for tax, income and diversification.
        </p>
      </div>
      <button class="mf-link-btn" type="button" @click="emit('edit')" :disabled="loading">
        ← Change answers
      </button>
    </div>

    <div v-if="loading" class="mf-loading-box">
      <div class="mf-spinner" />
      <p>Building your portfolio...</p>
    </div>

    <template v-else>
      <!-- Answers accordion at top -->
      <details class="mf-answers" :open="showAnswers">
        <summary @click.prevent="showAnswers = !showAnswers">
          {{ showAnswers ? 'Hide your answers' : 'See your answers' }}
        </summary>
        <div v-if="showAnswers" class="mf-answers-grid">
          <div v-for="(row, idx) in prettyAnswers" :key="idx" class="mf-answers-row">
            <div class="q">{{ row.label }}</div>
            <div class="a">{{ row.value ?? 'Not provided' }}</div>
          </div>
        </div>
      </details>

      <!-- Top section unchanged -->
      <div class="mf-top">
        <div class="mf-donut-wrap">
          <Chart type="doughnut" :data="pieData" :options="pieOptions" />
        </div>
        <div class="mf-legend-stack">
          <div class="mf-legend-block">
            <div class="mf-legend-title">Anchor Funds</div>
            <div class="mf-chip-row">
              <span
                v-for="(f, idx) in anchors"
                :key="'ac-' + idx"
                class="mf-chip mf-chip-anchor"
              >
                {{ f.category }}
              </span>
            </div>
          </div>
          <div class="mf-legend-block">
            <div class="mf-legend-title">Satellite Funds</div>
            <div class="mf-chip-row">
              <span
                v-for="(f, idx) in satellites"
                :key="'sat-' + idx"
                class="mf-chip mf-chip-satellite"
              >
                {{ f.category }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Paired rows: header + cards -->
      <div class="mf-rows">
        <div class="mf-row mf-row-header">
          <div class="mf-col-title">Anchors</div>
          <div class="mf-col-title">Satellites</div>
        </div>
        <div
          v-for="(row, idx) in rows"
          :key="idx"
          class="mf-row"
        >
          <div class="mf-cell">
            <div
              v-if="row.anchor"
              class="mf-card mf-card-anchor"
            >
              <div class="mf-pill mf-pill-anchor">Anchor</div>
              <h4 class="mf-fund-name">
                {{ row.anchor.fundName || row.anchor.category }}
                <i class="pi pi-link mf-fund-link" style="margin-left:6px;cursor:pointer;" @click.stop="openMFDialog(row.anchor)" title="View details" />
              </h4>
              <p class="mf-fund-category">{{ row.anchor.category }}</p>
              <div class="mf-fund-allocation">{{ row.anchor.allocationPercent?.toFixed(2) ?? '0.00' }}%</div>
              <p class="mf-fund-why">{{ row.anchor.why }}</p>
              <div class="mf-fund-meta">
                <span v-if="row.anchor.risk">Risk: {{ row.anchor.risk }}</span>
                <span v-if="row.anchor.sharpe3Y">Sharpe (3Y): {{ row.anchor.sharpe3Y }}</span>
              </div>
            </div>
          </div>
          <div class="mf-cell">
            <div
              v-if="row.satellite"
              class="mf-card mf-card-satellite"
            >
              <div class="mf-pill mf-pill-satellite">Satellite</div>
              <h4 class="mf-fund-name">
                {{ row.satellite.fundName || row.satellite.category }}
                <i class="pi pi-link mf-fund-link" style="margin-left:6px;cursor:pointer;" @click.stop="openMFDialog(row.satellite)" title="View details" />
              </h4>
              <p class="mf-fund-category">{{ row.satellite.category }}</p>
              <div class="mf-fund-allocation">{{ row.satellite.allocationPercent?.toFixed(2) ?? '0.00' }}%</div>
              <p class="mf-fund-why">{{ row.satellite.why }}</p>
              <div class="mf-fund-meta">
                <span v-if="row.satellite.risk">Risk: {{ row.satellite.risk }}</span>
                <span v-if="row.satellite.sharpe3Y">Sharpe (3Y): {{ row.satellite.sharpe3Y }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.mf-result-root {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.mf-result-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.mf-title {
  margin: 0;
  font-size: 1.2rem;
}

.mf-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: var(--an-text-muted);
}

.mf-link-btn {
  border: none;
  background: transparent;
  color: var(--an-primary, #1976d2);
  font-size: 0.8rem;
  cursor: pointer;
}

.mf-loading-box {
  border-radius: 8px;
  border: 1px solid #e9ecef;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.mf-spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid #e9ecef;
  border-top-color: var(--an-primary, #1976d2);
  animation: spin 1s linear infinite;
}

.mf-top {
  display: flex;
  gap: 32px;
  align-items: center;
}

.mf-donut-wrap {
  width: 220px;
  height: 220px;
}

.mf-legend-stack {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mf-legend-block {
  background: #f8fafc;
  border-radius: 10px;
  padding: 10px 12px;
}

.mf-legend-title {
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.mf-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mf-chip {
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 4px 10px;
}

.mf-chip-anchor {
  background: #dbeafe;
  color: #1d4ed8;
}

.mf-chip-satellite {
  background: #dcfce7;
  color: #15803d;
}

.mf-answers {
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  padding: 8px 12px;
  background: #f9fafb;
}

.mf-answers summary {
  cursor: pointer;
  font-size: 0.86rem;
  font-weight: 600;
}

.mf-answers-grid {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 8px 16px;
}

.mf-answers-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.q {
  font-size: 0.78rem;
  color: #868e96;
}

.a {
  font-size: 0.87rem;
  font-weight: 600;
}

.mf-rows {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mf-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 32px;
  align-items: flex-start; /* top-align cards in each row */
}

.mf-row-header {
  font-weight: 700;
}

.mf-cell {
  display: flex;
}

.mf-col-title {
  font-size: 1rem;
}

.mf-card {
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.mf-card-anchor {
  background: #f1f8ff;
  border: 1px solid #cce4ff;
}

.mf-card-satellite {
  background: #f3fbf4;
  border: 1px solid #d3f9d8;
}

.mf-pill {
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
}

.mf-pill-anchor {
  background: #cce4ff;
  color: #084298;
}

.mf-pill-satellite {
  background: #d3f9d8;
  color: #0f5132;
}

.mf-fund-name {
  margin: 2px 0 0 0;
  font-size: 0.95rem;
}

.mf-fund-category {
  margin: 0;
  font-size: 0.78rem;
  color: #868e96;
}

.mf-fund-allocation {
  font-size: 0.9rem;
  font-weight: 700;
}

.mf-fund-why {
  margin: 0;
  font-size: 0.78rem;
}

.mf-fund-meta {
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  font-size: 0.75rem;
  color: #495057;
}

.mf-fund-link {
  color: var(--an-primary);
  font-size: 1.1em;
  vertical-align: middle;
}

@media (max-width: 900px) {
  .mf-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .mf-row {
    grid-template-columns: minmax(0, 1fr);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
